// Sync Controls Component
// Provides sync toggle and manual action controls

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { 
  RefreshCw, 
  Clock, 
  Loader2, 
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Calendar
} from 'lucide-react';
import Icon from '@/components/ui/Icon';

/**
 * Sync Controls Component
 * Provides daily sync toggle and manual sync actions
 */
const SyncControls = ({
  syncEnabled,
  onToggleSync,
  onRescanHistorical,
  onSyncNow,
  isProcessing = false,
  lastSyncDate = null,
  totalCampsFound = 0
}) => {
  /**
   * Format last sync date for display
   */
  const formatLastSyncDate = (date) => {
    if (!date) return 'Never';
    
    const syncDate = new Date(date);
    const now = new Date();
    const diffMs = now - syncDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return syncDate.toLocaleDateString();
  };

  /**
   * Get next sync time
   */
  const getNextSyncTime = () => {
    if (!syncEnabled) return 'Disabled';
    
    const now = new Date();
    const nextSync = new Date(now);
    nextSync.setHours(9, 0, 0, 0); // 9 AM
    
    // If it's already past 9 AM today, set for tomorrow
    if (now.getHours() >= 9) {
      nextSync.setDate(nextSync.getDate() + 1);
    }
    
    return `Daily at ${nextSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="space-y-6">
      {/* Daily Sync Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Daily Sync Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Switch
                  checked={syncEnabled}
                  onCheckedChange={onToggleSync}
                  disabled={isProcessing}
                />
                <span className="font-medium">Daily email sync</span>
              </div>
              <p className="text-sm text-gray-600">
                Automatically check for new camp emails each day
              </p>
            </div>
            
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                {getNextSyncTime()}
              </div>
              <div className="text-xs text-gray-500">
                Next scheduled sync
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            Manual Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Sync Now */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-blue-600" />
                  <span className="font-medium">Sync Now</span>
                </div>
                <p className="text-sm text-gray-600">
                  Check for new emails today
                </p>
              </div>
              <Button
                onClick={onSyncNow}
                disabled={isProcessing}
                variant="outline"
                size="sm"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Sync Now
              </Button>
            </div>

            {/* Re-scan Historical */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-brand-600" />
                  <span className="font-medium">Re-scan Historical Emails</span>
                </div>
                <p className="text-sm text-gray-600">
                  Process last 2 years again
                </p>
              </div>
              <Button
                onClick={onRescanHistorical}
                disabled={isProcessing}
                variant="outline"
                size="sm"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <BarChart3 className="h-4 w-4 mr-2" />
                )}
                Re-scan
              </Button>
            </div>
          </div>

          {/* Processing Status */}
          {isProcessing && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Processing emails...
                </span>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                This may take 1-2 minutes. You can continue using the app.
              </p>
            </div>
          )}

          {/* Last Sync Info */}
          {lastSyncDate && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-600">Last sync:</span>
                  <span className="font-medium">{formatLastSyncDate(lastSyncDate)}</span>
                </div>
                {totalCampsFound > 0 && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-green-700">{totalCampsFound} camps found</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            How Email Sync Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                <strong>Daily Sync:</strong> Automatically checks for new emails every day at 9 AM
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                <strong>Historical Scan:</strong> Processes up to 2 years of past emails to find existing camp information
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-brand-600 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                <strong>Privacy:</strong> We only read emails and never send, delete, or modify your messages
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                <strong>Processing:</strong> Email processing happens securely in the background and may take 1-2 minutes
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SyncControls;
